import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, CheckCircle, XCircle, Clock, ArrowUpLeft,
  Undo2, Ban, Receipt, ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import { getMyTransactions } from '../services/adminDealFlowApi';
import { Card, CardContent } from '../components/new-components/ui/card';
import { Button } from '../components/new-components/ui/button';
import { Badge } from '../components/new-components/ui/badge';
import { Input } from '../components/new-components/ui/input';
import { PortalContainer } from '../components/portal/PortalContainer';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/new-components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/new-components/ui/dialog';

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const formatDateTime = (d: string) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

const statusConfig: Record<string, { color: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode; label: string }> = {
  pending: { color: 'outline', icon: <Clock className="h-3.5 w-3.5 text-gray-400" />, label: 'Pending' },
  processing: { color: 'secondary', icon: <RefreshCw className="h-3.5 w-3.5 text-blue-500" />, label: 'Processing' },
  settled: { color: 'default', icon: <CheckCircle className="h-3.5 w-3.5 text-green-600" />, label: 'Settled' },
  failed: { color: 'destructive', icon: <XCircle className="h-3.5 w-3.5 text-red-500" />, label: 'Failed' },
  returned: { color: 'destructive', icon: <ArrowUpLeft className="h-3.5 w-3.5 text-red-500" />, label: 'Returned' },
  refunded: { color: 'secondary', icon: <Undo2 className="h-3.5 w-3.5 text-orange-500" />, label: 'Refunded' },
  partially_refunded: { color: 'secondary', icon: <Undo2 className="h-3.5 w-3.5 text-amber-500" />, label: 'Partial Refund' },
  cancelled: { color: 'outline', icon: <Ban className="h-3.5 w-3.5 text-gray-400" />, label: 'Cancelled' },
};

const MyPayments: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;

  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const [selected, setSelected] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);

  // `isActive` lets the mount/filter effect discard a stale response when the user changes
  // filters or pages before an earlier request resolves (out-of-order response race).
  const load = useCallback(async (isActive: () => boolean = () => true) => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (methodFilter !== 'all') params.method = methodFilter;
      const res = await getMyTransactions(params);
      if (!isActive()) return;
      setTransactions(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      if (isActive()) console.error('Failed to load transactions', err);
    } finally {
      if (isActive()) setLoading(false);
    }
  }, [page, statusFilter, methodFilter]);

  useEffect(() => {
    let active = true;
    load(() => active);
    return () => { active = false; };
  }, [load]);

  const totalPages = Math.ceil(total / limit);

  // Summary from loaded data
  const totalSettled = transactions.filter(t => t.status === 'settled').reduce((s, t) => s + (t.net_amount_cents || 0), 0);
  const totalPending = transactions.filter(t => ['pending', 'processing'].includes(t.status)).reduce((s, t) => s + (t.amount_cents || 0), 0);

  return (
    <PortalContainer className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold text-[#242424]">Transactions</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your transaction history and payment details
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Payments</p>
            <p className="text-lg font-semibold text-[#242424]">{total}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Settled</p>
            <p className="text-lg font-semibold text-green-700">{formatCurrency(totalSettled)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-lg font-semibold text-amber-600">{formatCurrency(totalPending)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">This Page</p>
            <p className="text-lg font-semibold text-[#242424]">{transactions.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="settled">Settled</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={v => { setMethodFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Method" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="ach">ACH</SelectItem>
            <SelectItem value="usdc">USDC</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => load()} className="gap-1 sm:ml-auto">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Transaction List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : transactions.length === 0 ? (
        <Card className="border-border">
          <CardContent className="p-12 text-center">
            <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No payments found</p>
            <p className="text-sm text-muted-foreground mt-1">Your payment history will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {transactions.map(txn => {
            const sc = statusConfig[txn.status] || statusConfig.pending;
            return (
              <Card
                key={txn.id}
                className="border-border hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => { setSelected(txn); setShowDetail(true); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="w-10 h-10 bg-[#F0F0F0] rounded-full flex items-center justify-center border border-border flex-shrink-0">
                        <Receipt className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-[#242424]">{txn.engagement_title || txn.deal_name || 'Payment'}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                          <span className="text-xs font-mono text-muted-foreground">{txn.txn_id}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</span>
                          {txn.source && (
                            <Badge variant="outline" className={`text-[10px] uppercase ${txn.source === 'investment' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                              {txn.source}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-sm font-semibold text-[#242424]">{formatCurrency(txn.amount_cents)}</p>
                      <div className="flex items-center gap-1 justify-end mt-0.5">
                        {sc.icon}
                        <Badge variant={sc.color} className="text-xs capitalize">
                          {sc.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs uppercase ml-1">{txn.method}</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Return / refund info inline */}
                  {(txn.return_code || (txn.refund_amount_cents || 0) > 0) && (
                    <div className="mt-2 pt-2 border-t border-border flex gap-3">
                      {txn.return_code && (
                        <span className="text-xs text-red-600">Return: {txn.return_code} — {txn.return_reason || 'ACH Return'}</span>
                      )}
                      {(txn.refund_amount_cents || 0) > 0 && (
                        <span className="text-xs text-orange-600">Refunded: {formatCurrency(txn.refund_amount_cents)}</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ===== Detail Dialog ===== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-md">
          {selected && (() => {
            const sc = statusConfig[selected.status] || statusConfig.pending;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Payment Details
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-semibold text-[#242424]">{formatCurrency(selected.amount_cents)}</p>
                      <p className="text-xs font-mono text-muted-foreground">{selected.txn_id}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {sc.icon}
                      <Badge variant={sc.color} className="capitalize text-sm">{sc.label}</Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">For</p>
                      <p className="text-[#242424]">{selected.engagement_title || selected.deal_name || '—'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{selected.source === 'investment' ? 'Deal Investment' : selected.engagement_type === 'one_time' ? 'One-time' : (selected.engagement_type || 'Payment')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Method</p>
                      <p className="text-[#242424] uppercase">{selected.method}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="text-[#242424]">{formatDateTime(selected.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Settled</p>
                      <p className="text-[#242424]">{formatDateTime(selected.settled_at)}</p>
                    </div>
                    {(selected.fees_cents || 0) > 0 && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground">Fees</p>
                          <p className="text-[#242424]">{formatCurrency(selected.fees_cents)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Net Amount</p>
                          <p className="text-[#242424]">{formatCurrency(selected.net_amount_cents)}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Payment method details */}
                  {selected.method === 'ach' && selected.ach_account_last4 && (
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <p className="text-xs text-muted-foreground">Bank Account</p>
                      <p className="text-[#242424]">****{selected.ach_account_last4}</p>
                    </div>
                  )}
                  {selected.method === 'usdc' && (selected.wallet_address || selected.tx_hash) && (
                    <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                      {selected.chain && <p className="text-xs text-muted-foreground">Chain: <span className="text-[#242424] capitalize">{selected.chain}</span></p>}
                      {selected.tx_hash && (
                        <div>
                          <p className="text-xs text-muted-foreground">Transaction Hash</p>
                          <p className="text-xs font-mono text-blue-600 break-all">{selected.tx_hash}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Return info */}
                  {selected.return_code && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-red-800">Returned: {selected.return_code}</p>
                      <p className="text-xs text-red-700">{selected.return_reason || 'Payment was returned by your bank'}</p>
                    </div>
                  )}

                  {/* Refund info */}
                  {(selected.refund_amount_cents || 0) > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-orange-800">Refund: {formatCurrency(selected.refund_amount_cents)}</p>
                      {selected.refund_reason && <p className="text-xs text-orange-700">{selected.refund_reason}</p>}
                      {selected.refunded_at && <p className="text-xs text-orange-600 mt-1">{formatDateTime(selected.refunded_at)}</p>}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </PortalContainer>
  );
};

export default MyPayments;
